import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyObject, ObjectSchema } from 'yup';

import { FormStepId } from 'shared/config/formStepId';

import {
  setIsStepFullFilledByStepId,
  WorkplaceFormValidationSchema,
  CreditPurposeFormValidationSchema,
  ContactsFormValidationSchema,
  PassportFormValidationSchema,
  AdditionalInfoFormValidationSchema,
  selectAllFormFields,
  setAutoValidationInProgress,
} from 'entities/creditForm';

import type { TSendCustomerDataThunkState } from 'features/SendCustomerData';
import { selectCreditPurposeFormViewOptions } from 'features/UpdateCreditPurposeInfo';

import type { TStepValidationErrors, TValidateUserDataThunkPayload } from './types';
import { validateStep } from './validateStep';

export const validateUserDataThunk = createAsyncThunk<
  void,
  TValidateUserDataThunkPayload,
  { state: TSendCustomerDataThunkState }
>(
  'validateUserData/validateUserDataThunk',
  // eslint-disable-next-line max-statements
  async ({ onComplete, onSuccess, onError, stepsToValidate, onStepSuccess }, { dispatch, getState }) => {
    dispatch(setAutoValidationInProgress(true));
    const steps = stepsToValidate || [
      FormStepId.CreditPurposeInfo,
      FormStepId.ContactsInfo,
      FormStepId.PassportInfo,
      FormStepId.WorkplaceInfo,
      FormStepId.AdditionalInfo,
    ];

    const formFields = selectAllFormFields(getState());
    const errorSteps: TStepValidationErrors[] = [];
    const creditPurposeViewOptions = selectCreditPurposeFormViewOptions(getState());

    const schemaByStep: Record<ValuesOf<typeof FormStepId>, () => ObjectSchema<AnyObject>> = {
      [FormStepId.CreditPurposeInfo]: () => CreditPurposeFormValidationSchema(creditPurposeViewOptions),
      [FormStepId.ContactsInfo]: () => ContactsFormValidationSchema(true),
      [FormStepId.PassportInfo]: () => PassportFormValidationSchema(),
      [FormStepId.WorkplaceInfo]: () => WorkplaceFormValidationSchema(),
      [FormStepId.AdditionalInfo]: () => AdditionalInfoFormValidationSchema(),
    };

    const successfullyValidatedSteps: Array<ValuesOf<typeof FormStepId>> = [];

    for (const stepId of steps) {
      // Валидация должна идти последовательно, чтобы остановиться на первом шаге с ошибкой
      // eslint-disable-next-line no-await-in-loop
      const { isValid, errorStep } = await validateStep(schemaByStep[stepId](), formFields, stepId);

      dispatch(
        setIsStepFullFilledByStepId({
          stepId,
          isFullFilled: isValid,
        }),
      );

      if (errorStep) {
        errorSteps.push(errorStep);
      }

      if (!isValid) {
        break;
      }

      successfullyValidatedSteps.push(stepId);
      // eslint-disable-next-line no-await-in-loop
      await onStepSuccess?.(stepId);
    }

    const isSuccess = successfullyValidatedSteps.length === steps.length;

    if (isSuccess) {
      const lastValidatedStepId = successfullyValidatedSteps[successfullyValidatedSteps.length - 1];
      await onSuccess?.(formFields, steps, lastValidatedStepId);
    } else {
      await onError?.(errorSteps, steps);
    }
    await onComplete?.();
    dispatch(setAutoValidationInProgress(false));
  },
);
